import { useEffect, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { PageHero } from "@/components/hero/PageHero";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { getLegalDocument, type LegalDocumentResponse } from "@/lib/api";

interface LegalDocViewProps {
  kind: string;
  eyebrow: string;
  title: string;
  /**
   * Interactive content rendered below the document. The body itself is
   * no-raw-HTML Markdown, so any control the policy describes has to be
   * a real component sitting outside it.
   */
  appendix?: ReactNode;
}

/**
 * Renders a versioned legal document fetched from the backend. The body is
 * Markdown rendered with react-markdown in its default (no-raw-HTML) mode, so
 * a template edit can never inject script into these public pages.
 */
export function LegalDocView({ kind, eyebrow, title, appendix }: LegalDocViewProps) {
  const [doc, setDoc] = useState<LegalDocumentResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    void getLegalDocument(kind)
      .then((d) => {
        if (active) setDoc(d);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [kind]);

  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink">
      <Header />
      <main className="flex-1">
        <PageHero eyebrow={eyebrow} title={title} size="sm" />
        <section className="bg-bg py-20">
          <div className="wrap max-w-3xl space-y-6 text-sm leading-relaxed text-muted [&_h2]:font-heading [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-ink [&_li]:ml-0 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6">
            {failed && (
              <p>This document is temporarily unavailable. Please try again shortly.</p>
            )}
            {!failed && !doc && <p className="text-ink-muted">Loading…</p>}
            {doc && (
              <>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.body}</ReactMarkdown>
                <p className="text-xs text-ink-hint">
                  Version {doc.version} · effective {doc.effective_date}
                </p>
              </>
            )}
            {appendix}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
